/**
 * Receipt Detail Drawer
 *
 * Drawer showing full details of a POS receipt with items and consumptions.
 */

import { ChevronDown, ChevronRight, Play, Loader2, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Drawer, Button, RetentionExpiredBanner } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes, formatPrice, formatQuantity } from '@/lib/format';
import { usePosReceiptDetail, useSyncByReceiptIds } from '../hooks';
import { useGeneratePOSMovements } from '@/features/stock-movements';
import type { PosReceiptItemDetail, TheoreticalConsumptionDetail, SaleType, Currency } from '@bstock/shared';
import { saleTypeLabels } from '@bstock/shared';

interface ReceiptDetailDrawerProps {
  receiptId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showFinancials?: boolean;
}

function SaleTypeBadge({ type }: { type: SaleType }) {
  const colors: Record<SaleType, string> = {
    STANDARD: 'bg-gray-100 text-gray-700',
    PROMO: 'bg-yellow-100 text-yellow-700',
    EMPLOYEE_MEAL: 'bg-green-100 text-green-700',
    OFFERED: 'bg-purple-100 text-purple-700',
    WASTE: 'bg-red-100 text-red-700',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[type]}`}>
      {saleTypeLabels[type]}
    </span>
  );
}

interface ReceiptItemRowProps {
  item: PosReceiptItemDetail;
  consumptions: TheoreticalConsumptionDetail[];
  currency: Currency;
  showFinancials: boolean;
}

function ReceiptItemRow({ item, consumptions, currency, showFinancials }: ReceiptItemRowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasConsumptions = consumptions.length > 0;
  const hasWarning = !item.recipeVersionId || !hasConsumptions;

  return (
    <>
      <tr
        className={`${hasWarning ? 'bg-orange-50' : ''} ${hasConsumptions ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={() => hasConsumptions && setExpanded(!expanded)}
      >
        <td className="px-3 py-2 text-center">
          {hasConsumptions ? (
            expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
          ) : (
            <span className="w-4 h-4 inline-block" />
          )}
        </td>
        <td className="px-3 py-2 text-sm">
          {item.posProductName}
          {hasWarning && (
            <span className="ml-2 text-orange-500 text-xs">(sans recette)</span>
          )}
        </td>
        <td className="px-3 py-2 text-sm text-center">{item.quantity}</td>
        <td className="px-3 py-2 text-sm text-center">{item.posMenuName || '-'}</td>
        {showFinancials && (
          <td className="px-3 py-2 text-sm text-right">
            {item.discountAmount > 0 ? formatCentimes(-item.discountAmount, currency) : '-'}
          </td>
        )}
        <td className="px-3 py-2 text-center">
          <SaleTypeBadge type={item.saleType} />
        </td>
        {showFinancials && (
          <>
            <td className="px-3 py-2 text-sm text-right">
              {formatCentimes(item.unitPrice * item.quantity - item.discountAmount, currency)}
            </td>
            <td className="px-3 py-2 text-sm text-right">
              {item.theoreticalCostAtSale !== null
                ? formatCentimes(item.theoreticalCostAtSale * item.quantity, currency)
                : 'N/A'}
            </td>
          </>
        )}
      </tr>

      {/* Expanded consumption details - only for managers+ */}
      {showFinancials && expanded && consumptions.length > 0 && (
        <tr>
          <td colSpan={showFinancials ? 8 : 4} className="px-8 py-2 bg-blue-50">
            <div className="text-xs font-medium text-gray-600 mb-2">Consommation theorique</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="text-left py-1">Produit brut</th>
                  <th className="text-right py-1">Quantite</th>
                  <th className="text-right py-1">PMP</th>
                  <th className="text-right py-1">Cout</th>
                </tr>
              </thead>
              <tbody>
                {consumptions.map((c) => (
                  <tr key={c.id}>
                    <td className="py-1">{c.rawProductName}</td>
                    <td className="text-right">{formatQuantity(c.quantity)} {c.rawProductUnit}</td>
                    <td className="text-right">
                      {c.pmpValue !== undefined
                        ? `${formatPrice(c.pmpValue, currency)}/${c.displayUnit}`
                        : '-'}
                    </td>
                    <td className="text-right">
                      {c.theoreticalCostInEuros !== undefined
                        ? formatPrice(c.theoreticalCostInEuros, currency)
                        : formatCentimes(c.theoreticalCost, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-medium border-t border-blue-200">
                  <td colSpan={3} className="text-right py-1">Total:</td>
                  <td className="text-right py-1">
                    {formatCentimes(consumptions.reduce((sum, c) => sum + c.theoreticalCost, 0), currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export function ReceiptDetailDrawer({ receiptId, open, onOpenChange, showFinancials = false }: ReceiptDetailDrawerProps) {
  const currency = useCurrency();
  const { formatDateTime } = useFormatDate();
  const queryClient = useQueryClient();
  const { data, isLoading } = usePosReceiptDetail(receiptId);
  const generateMovements = useGeneratePOSMovements();
  const resyncByIds = useSyncByReceiptIds();

  const handleGenerateMovements = () => {
    if (!receiptId) return;
    generateMovements.mutate(
      { receiptIds: [receiptId] },
      {
        onSuccess: () => {
          // Invalidate the receipt detail to refresh hasMovements status
          queryClient.invalidateQueries({ queryKey: ['pos-receipts'] });
          queryClient.invalidateQueries({ queryKey: ['pos-receipt', receiptId] });
        },
      }
    );
  };

  const handleResyncTicket = () => {
    if (!data?.receipt.posReceiptId) return;
    resyncByIds.mutate([data.receipt.posReceiptId]);
  };

  const title = data
    ? `Inspection de ticket - ${data.receipt.orderId}`
    : 'Inspection de ticket';

  const description = data
    ? formatDateTime(data.receipt.createdAt)
    : undefined;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="wide"
    >
      {isLoading && (
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-gray-100 rounded" />
          <div className="h-48 bg-gray-100 rounded" />
        </div>
      )}

      {data && (
        <div className="space-y-6">
          {/* Retention expired: le detail par ingredient a ete purge pour ce
              ticket (US-07/US-09). Le reste du drawer (items, montants,
              marge) reste affiche normalement, seule la section
              "Consommation theorique" de chaque ligne apparaitra vide. */}
          {data._meta?.retentionExpired && (
            <RetentionExpiredBanner
              retentionMonths={data._meta.retentionMonths}
              resource="du ticket"
            />
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs font-medium text-gray-500">ID externe</div>
              <div className="text-sm font-mono truncate">{data.receipt.externalId}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs font-medium text-gray-500">Terminal POS</div>
              <div className="text-sm">{data.receipt.posTerminal || '-'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs font-medium text-gray-500">Utilisateur POS</div>
              <div className="text-sm">{data.receipt.posUsername || '-'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs font-medium text-gray-500">Batch d'import</div>
              <div className="text-sm font-mono truncate">
                {data.receipt.importBatchId?.slice(0, 8) || '-'}
              </div>
            </div>
            {showFinancials && (
              <>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs font-medium text-gray-500">Total HT</div>
                  <div className="text-sm font-semibold">{formatCentimes(data.receipt.amountExclTax, currency)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs font-medium text-gray-500">Total TTC</div>
                  <div className="text-sm font-semibold">{formatCentimes(data.receipt.amountInclTax, currency)}</div>
                </div>
              </>
            )}
          </div>

          {/* Bloc Bill posReceiptId + bouton resync unitaire */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-medium text-blue-700">ID Bill (posReceiptId)</div>
              <div className="text-sm font-mono truncate text-blue-900">
                {data.receipt.posReceiptId ?? '(non renseigne — ticket historique)'}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleResyncTicket}
              disabled={!data.receipt.posReceiptId || resyncByIds.isPending}
              title={
                !data.receipt.posReceiptId
                  ? 'Ce ticket a ete synchronise avant le rollout du champ ID Bill. Utilisez la re-synchro par periode.'
                  : 'Rejoue la synchro pour ce ticket uniquement'
              }
            >
              {resyncByIds.isPending ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1.5" />
              )}
              Resynchroniser
            </Button>
          </div>

          {/* Cancelled badge */}
          {data.receipt.isCancel && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm font-medium">
              Ce ticket a ete annule
            </div>
          )}

          {/* Movements status */}
          <div className={`rounded-lg p-3 flex items-center justify-between ${
            data.receipt.hasMovements
              ? 'bg-green-50 border border-green-200'
              : 'bg-orange-50 border border-orange-200'
          }`}>
            <div className="flex items-center gap-2">
              {data.receipt.hasMovements ? (
                <>
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 text-sm font-medium">
                    Mouvements de stock generes
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <span className="text-orange-700 text-sm font-medium">
                    Mouvements de stock non generes
                  </span>
                </>
              )}
            </div>
            {!data.receipt.hasMovements && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleGenerateMovements}
                disabled={generateMovements.isPending}
              >
                {generateMovements.isPending ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-1.5" />
                )}
                Generer
              </Button>
            )}
          </div>

          {/* Items table */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Articles vendus</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-xs text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left w-8"></th>
                    <th className="px-3 py-2 text-left">Produit</th>
                    <th className="px-3 py-2 text-center">Qte</th>
                    <th className="px-3 py-2 text-center">Menu</th>
                    {showFinancials && (
                      <th className="px-3 py-2 text-right">Remise</th>
                    )}
                    <th className="px-3 py-2 text-center">Type</th>
                    {showFinancials && (
                      <>
                        <th className="px-3 py-2 text-right">CA HT</th>
                        <th className="px-3 py-2 text-right">Coût HT</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.items.map((item) => (
                    <ReceiptItemRow
                      key={item.id}
                      item={item}
                      consumptions={data.consumptions[item.id] || []}
                      currency={currency}
                      showFinancials={showFinancials}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Articles avec recette:</span>
              <span className="font-medium">{data.receipt.itemsWithRecipeCount}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-600">Articles sans recette:</span>
              <span className="font-medium text-orange-600">{data.receipt.itemsWithoutRecipeCount}</span>
            </div>
            {showFinancials && data.receipt.theoreticalCostTotal !== null && (
              <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200">
                <span className="font-medium text-gray-900">Cout theorique total:</span>
                <span className="font-semibold">{formatCentimes(data.receipt.theoreticalCostTotal, currency)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
